import { RotationPage } from './pages/WorkforceParts';
import { lazy, Suspense } from 'react';
import type React from 'react';
import { LOCKABLE_KEYS } from '@cip/domain';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from './lib/api';
import { ProjectProvider, useProject } from './lib/project';
import { Shell } from './components/Shell';
import { Login } from './pages/Login';
import { Loading } from './components/ui';
import { InvitePage, SharedPod } from './pages/Public';

const SiteOps = lazy(() => import('./pages/SiteOps'));
const PlanToExecute = lazy(() => import('./pages/PlanToExecute'));
const Turbines = lazy(() => import('./pages/Turbines'));
const TurbineRecord = lazy(() => import('./pages/TurbineRecord'));
const Schedule = lazy(() => import('./pages/Schedule'));
const Pod = lazy(() => import('./pages/Pod'));
const Field = lazy(() => import('./pages/Field'));
const Workforce = lazy(() => import('./pages/Workforce'));
const Equipment = lazy(() => import('./pages/Equipment'));
const Procurement = lazy(() => import('./pages/Procurement'));
const Subcontracts = lazy(() => import('./pages/Subcontracts'));
const Commercial = lazy(() => import('./pages/Commercial'));
const Delays = lazy(() => import('./pages/Delays'));
const Pwa = lazy(() => import('./pages/Pwa'));
const Safety = lazy(() => import('./pages/Safety'));
const Quality = lazy(() => import('./pages/Quality'));
const Reports = lazy(() => import('./pages/Reports'));
const Imports = lazy(() => import('./pages/Imports'));
const Assistant = lazy(() => import('./pages/Assistant'));
const Admin = lazy(() => import('./pages/Admin'));
const Inbox = lazy(() => import('./pages/Inbox'));
const Decisions = lazy(() => import('./pages/Decisions'));

export function useMe() {
  return useQuery({ queryKey: ['me'], queryFn: () => api.get<{ user: any; projects: any[] }>('/api/auth/me'), retry: false, staleTime: 60_000 });
}

/** A page locked for this person shows a locked notice instead (the server refuses its data too). */
function LockGate({ children }: { children: React.ReactNode }) {
  const { info, pid } = useProject(); const loc = useLocation();
  const locked = info.me.lockedPages ?? [];
  const seg = loc.pathname.split('/')[3] ?? '';
  if (!locked.length) return <>{children}</>;
  if (!seg && locked.includes('pod')) {
    const first = LOCKABLE_KEYS.find((k) => !locked.includes(k));
    return first ? <Navigate to={`/p/${pid}/${first}`} replace /> : <div className="page"><h1>No pages available</h1><p className="muted">Every page on this project is locked for you. Ask the project manager.</p></div>;
  }
  if (locked.includes(seg) && !(seg === 'admin' && new URLSearchParams(loc.search).get('tab') === 'account')) return <div className="page"><h1>This page is locked</h1><p className="muted">The project manager has locked this page for you on this project. Ask them if you need access.</p></div>;
  return <>{children}</>;
}

function ProjectRoutes() {
  const { pid } = useParams();
  return (
    <ProjectProvider pid={pid!}>
      <Shell>
        <LockGate>
        <Suspense fallback={<div className="page"><Loading /></div>}>
          <Routes>
            <Route index element={<Navigate to="pod" replace />} />
            <Route path="command" element={<Navigate to="../pod" replace />} />
            <Route path="site" element={<SiteOps />} />
            <Route path="plan" element={<PlanToExecute />} />
            <Route path="turbines" element={<Turbines />} />
            <Route path="turbines/:tid" element={<TurbineRecord />} />
            <Route path="schedule" element={<Schedule />} />
            <Route path="pod" element={<Pod />} />
            <Route path="field" element={<Field />} />
            <Route path="workforce" element={<Workforce />} />
            <Route path="rotation" element={<RotationPage />} />
            <Route path="equipment" element={<Equipment />} />
            <Route path="procurement" element={<Procurement />} />
            <Route path="subcontracts" element={<Subcontracts />} />
            <Route path="commercial" element={<Commercial />} />
            <Route path="delays" element={<Delays />} />
            <Route path="pwa" element={<Pwa />} />
            <Route path="safety" element={<Safety />} />
            <Route path="quality" element={<ModuleGate module="quality" label="Quality"><Quality /></ModuleGate>} />
            <Route path="completion" element={<Navigate to="../turbines" replace />} />
            <Route path="contract" element={<Navigate to="../plan?tab=obligations" replace />} />
            <Route path="reports" element={<Reports />} />
            <Route path="imports" element={<Imports />} />
            <Route path="assistant" element={<Assistant />} />
            <Route path="decisions" element={<Decisions />} />
            <Route path="inbox" element={<Inbox />} />
            <Route path="admin" element={<Admin />} />
            <Route path="*" element={<div className="page"><h1>Not found</h1></div>} />
          </Routes>
        </Suspense>
        </LockGate>
      </Shell>
    </ProjectProvider>
  );
}

/** Public pages (shared POD, invite link) render without a session; everything else requires sign-in. */
export function App() {
  const loc = useLocation();
  if (loc.pathname.startsWith('/share/') || loc.pathname.startsWith('/invite/')) {
    return <Routes><Route path="/share/:token" element={<SharedPod />} /><Route path="/invite/:token" element={<InvitePage />} /></Routes>;
  }
  return <SignedInApp />;
}

function SignedInApp() {
  const me = useMe();
  if (me.isLoading) return <div className="page"><Loading /></div>;
  if (me.error && (me.error as ApiError).status === 401) return <Routes><Route path="*" element={<Login />} /></Routes>;
  if (me.error) return <div className="page"><div className="banner crit">Cannot reach the server: {(me.error as Error).message}</div></div>;
  const first = me.data!.projects.find((p) => p.environment === 'live') ?? me.data!.projects[0];
  return (
    <Routes>
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route path="/p/:pid/*" element={<ProjectRoutes />} />
      <Route path="*" element={first ? <Navigate to={`/p/${first.id}/pod`} replace /> : <div className="page"><h1>No project access</h1><p className="muted">Ask a project administrator to add you to a project.</p></div>} />
    </Routes>
  );
}

/** A page whose module is switched off for this project says so instead of rendering empty. */
function ModuleGate({ module, label, children }: { module: string; label: string; children: JSX.Element }) {
  const { info, pid } = useProject();
  if (info.project?.settings?.modules?.[module] === false) return <div className="page"><div className="banner info">{label} is switched off for this project. A project admin can turn it on in <a href={`/p/${pid}/admin?tab=project`}>Admin → Modules &amp; POD branding</a>.</div></div>;
  return children;
}
